import { useState } from 'react';
import { Box, Button, Collapse, FormControl, IconButton, MenuItem, Select, Switch, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckIcon from '@mui/icons-material/Check';
import type {
    IDeliverable,
    DeliverableType,
    DeliverablePlatform,
    DeliverableDuration,
} from '../../redux/buzzbridge/types';
import { DELIVERABLE_TYPE_LABELS } from '../../utils/deliverableLabels';

const TYPES = [
    { value: 'instagram_post', label: 'Instagram Post', platform: 'instagram' },
    { value: 'instagram_reel', label: 'Instagram Reel', platform: 'instagram' },
    { value: 'instagram_story', label: 'Instagram Story', platform: 'instagram' },
    { value: 'tiktok_video', label: 'TikTok Video', platform: 'tiktok' },
    { value: 'tiktok_short', label: 'TikTok Short', platform: 'tiktok' },
    { value: 'tiktok_short', label: 'TikTok Short', platform: 'tiktok' },
    { value: 'youtube_short', label: 'YouTube Short', platform: 'youtube' },
    { value: 'youtube_video', label: 'YouTube Video', platform: 'youtube' },
    { value: 'twitter_post', label: 'Twitter / X Post', platform: 'twitter' },
    { value: 'facebook_post', label: 'Facebook Post', platform: 'facebook' },
    { value: 'ugc_video', label: 'UGC Video' },
    { value: 'ugc_photo', label: 'UGC Photo' },
    { value: 'vertical', label: 'Vertical Video', directHireOnly: true },
    { value: 'horizontal', label: 'Horizontal Video', directHireOnly: true },
];

const PLATFORMS = [
    { value: 'instagram', label: 'Instagram' },
    { value: 'tiktok', label: 'TikTok' },
    { value: 'youtube', label: 'YouTube' },
    { value: 'twitter', label: 'Twitter' },
    { value: 'x', label: 'X' },
    { value: 'facebook', label: 'Facebook' },
];

const DURATIONS = [
    { value: 'n/a', label: 'N/A' },
    { value: '0-15s', label: '0 – 15s' },
    { value: '15-30s', label: '15 – 30s' },
    { value: '30s-1min', label: '30s – 1 min' },
    { value: '1min-3min', label: '1 – 3 min' },
    { value: '3min-5min', label: '3 – 5 min' },
    { value: '5min+', label: '5 min+' },
];

const PLATFORM_COLORS: Record<string, string> = {
    instagram: '#c13584',
    tiktok: '#010101',
    youtube: '#ff0000',
    twitter: '#1da1f2',
    x: '#000000',
    facebook: '#1877f2',
};

const PLATFORM_BG: Record<string, string> = {
    instagram: '#c1358418',
    tiktok: '#69c9d018',
    youtube: '#ff000018',
    twitter: '#1da1f218',
    x: '#ffffff18',
    facebook: '#1877f218',
};

const DEFAULT_FORM = {
    type: '' as DeliverableType | '',
    platform: '' as DeliverablePlatform | '',
    duration_range: 'n/a' as DeliverableDuration,
    notes: '',
    multiUploadEnabled: false,
    multiUploadCount: 2,
    multiUploadLabels: ['', ''] as string[],
};

const labelSx = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase' as const,
    mb: 0.5,
    color: '#9e9e9e',
};

const selectSx = {
    bgcolor: '#1e1e1e',
    color: '#fff',
    borderRadius: 1.5,
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& .MuiSelect-icon': { color: '#9e9e9e' },
};

const menuProps = {
    PaperProps: {
        sx: {
            bgcolor: '#1e1e1e',
            color: '#fff',
            '& .MuiMenuItem-root:hover': { bgcolor: '#2e2e2e' },
        },
    },
};

interface Props {
    items: IDeliverable[];
    onItemsChange: (items: IDeliverable[]) => void;
    selectedPlatforms?: string[];
    isDirectHire?: boolean;
}

export function DeliverableInlineEditor({ items, onItemsChange, selectedPlatforms, isDirectHire = false }: Props) {
    const [addOpen, setAddOpen] = useState(false);
    const [editIndex, setEditIndex] = useState<number | null>(null);
    const [form, setForm] = useState({ ...DEFAULT_FORM });

    const hasPlatformFilter = selectedPlatforms && selectedPlatforms.length > 0;
    const filteredTypes = isDirectHire
        ? TYPES.filter(t => t.value === 'vertical' || t.value === 'horizontal')
        : hasPlatformFilter
        ? TYPES.filter(t => !t.platform || selectedPlatforms!.includes(t.platform))
        : TYPES.filter(t => !t.directHireOnly);
    const filteredPlatforms = hasPlatformFilter
        ? PLATFORMS.filter(p => selectedPlatforms!.includes(p.value))
        : PLATFORMS;

    const set = <K extends keyof typeof form>(key: K, val: (typeof form)[K]) =>
        setForm(prev => ({ ...prev, [key]: val }));

    const handleTypeChange = (type: DeliverableType) => {
        const meta = TYPES.find(t => t.value === type);
        setForm(prev => ({
            ...prev,
            type,
            platform: (meta?.platform as DeliverablePlatform) ?? prev.platform,
        }));
    };

    const openAdd = () => {
        setForm({ ...DEFAULT_FORM });
        setEditIndex(null);
        setAddOpen(true);
    };

    const openEdit = (i: number) => {
        const d = items[i];
        const mu = d.multiple_uploads;
        setForm({
            type: d.type,
            platform: d.platform ?? '',
            duration_range: d.duration_range,
            notes: d.notes ?? '',
            multiUploadEnabled: mu?.enabled ?? false,
            multiUploadCount: mu?.count ?? 2,
            multiUploadLabels: mu?.labels?.length ? [...mu.labels] : ['', ''],
        });
        setEditIndex(i);
        setAddOpen(false);
    };

    const handleCancel = () => {
        setAddOpen(false);
        setEditIndex(null);
        setForm({ ...DEFAULT_FORM });
    };

    const multiLabelsValid = !form.multiUploadEnabled ||
        form.multiUploadLabels.slice(0, form.multiUploadCount).every(l => l.trim() !== '');

    const canSave = form.type !== '' && (isDirectHire || form.platform !== '') && multiLabelsValid;

    const handleMultiUploadCountChange = (newCount: number) => {
        setForm(prev => {
            const labels = [...prev.multiUploadLabels];
            while (labels.length < newCount) labels.push('');
            return { ...prev, multiUploadCount: newCount, multiUploadLabels: labels };
        });
    };

    const handleSave = () => {
        if (!canSave) return;
        const d: IDeliverable = {
            type: form.type as DeliverableType,
            ...(form.platform && { platform: form.platform as DeliverablePlatform }),
            duration_range: form.duration_range,
            count: 1,
            ...(form.notes?.trim() && { notes: form.notes.trim() }),
            ...(form.multiUploadEnabled && {
                multiple_uploads: {
                    enabled: true,
                    count: form.multiUploadCount,
                    labels: form.multiUploadLabels.slice(0, form.multiUploadCount).map(l => l.trim()),
                },
            }),
        };
        if (editIndex !== null) {
            const updated = [...items];
            updated[editIndex] = d;
            onItemsChange(updated);
        } else {
            onItemsChange([...items, d]);
        }
        handleCancel();
    };

    const handleRemove = (i: number) => {
        if (editIndex === i) handleCancel();
        onItemsChange(items.filter((_, idx) => idx !== i));
    };

    const isFormOpen = addOpen || editIndex !== null;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Existing items */}
            {items.map((d, i) => {
                const color = d.platform ? PLATFORM_COLORS[d.platform] ?? '#9e9e9e' : '#9e9e9e';
                const bg = d.platform ? PLATFORM_BG[d.platform] ?? '#9e9e9e18' : '#9e9e9e18';
                const isEditing = editIndex === i;
                return (
                    <Box key={i}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                p: 1.25,
                                borderRadius: 1.5,
                                bgcolor: isEditing ? '#2a2a2a' : bg,
                                border: `1px solid ${isEditing ? '#ffc96540' : color + '30'}`,
                            }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>
                                    {DELIVERABLE_TYPE_LABELS[d.type] ?? d.type}
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: '#9e9e9e', mt: 0.2 }}>
                                    {d.platform ? d.platform.charAt(0).toUpperCase() + d.platform.slice(1) : ''}
                                    {d.duration_range !== 'n/a' ? `${d.platform ? ' · ' : ''}${d.duration_range}` : ''}
                                    {d.notes ? ` · ${d.notes}` : ''}
                                </Typography>
                            </Box>
                            <IconButton
                                size="small"
                                onClick={() => (isEditing ? handleCancel() : openEdit(i))}
                                sx={{ color: isEditing ? '#ffc965' : '#666', '&:hover': { color: '#fff' } }}>
                                {isEditing ? <CloseIcon fontSize="small" /> : <EditOutlinedIcon fontSize="small" />}
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={() => handleRemove(i)}
                                sx={{ color: '#666', '&:hover': { color: '#ef5350' } }}>
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Box>

                        {/* Inline edit form for this item */}
                        <Collapse in={isEditing} unmountOnExit>
                            <InlineForm
                                form={form}
                                set={set}
                                onTypeChange={handleTypeChange}
                                filteredTypes={filteredTypes}
                                filteredPlatforms={filteredPlatforms}
                                canSave={canSave}
                                onSave={handleSave}
                                onCancel={handleCancel}
                                isEdit
                                showPlatform={!isDirectHire}
                                onMultiUploadCountChange={handleMultiUploadCountChange}
                            />
                        </Collapse>
                    </Box>
                );
            })}

            {/* Add button */}
            {!isFormOpen && (
                <Button
                    startIcon={<AddIcon />}
                    onClick={openAdd}
                    sx={{
                        alignSelf: 'flex-start',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: 13,
                        color: '#ffc965',
                        px: 0,
                        '&:hover': { bgcolor: 'transparent', opacity: 0.8 },
                    }}>
                    Add deliverable
                </Button>
            )}

            {/* Inline add form */}
            <Collapse in={addOpen} unmountOnExit>
                <InlineForm
                    form={form}
                    set={set}
                    onTypeChange={handleTypeChange}
                    filteredTypes={filteredTypes}
                    filteredPlatforms={filteredPlatforms}
                    canSave={canSave}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    isEdit={false}
                    showPlatform={!isDirectHire}
                    onMultiUploadCountChange={handleMultiUploadCountChange}
                />
            </Collapse>
        </Box>
    );
}

function InlineForm({
    form,
    set,
    onTypeChange,
    filteredTypes,
    filteredPlatforms,
    canSave,
    onSave,
    onCancel,
    isEdit,
    showPlatform = true,
    onMultiUploadCountChange,
}: {
    form: typeof DEFAULT_FORM;
    set: <K extends keyof typeof DEFAULT_FORM>(key: K, val: (typeof DEFAULT_FORM)[K]) => void;
    onTypeChange: (type: DeliverableType) => void;
    filteredTypes: typeof TYPES;
    filteredPlatforms: typeof PLATFORMS;
    canSave: boolean;
    onSave: () => void;
    onCancel: () => void;
    isEdit: boolean;
    showPlatform?: boolean;
    onMultiUploadCountChange: (n: number) => void;
}) {
    return (
        <Box
            sx={{
                mt: 0.75,
                p: 1.5,
                borderRadius: 1.5,
                bgcolor: '#1a1a1a',
                border: '1px solid #2a2a2a',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
            }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                {/* Type */}
                <Box>
                    <Typography sx={labelSx}>Content type</Typography>
                    <FormControl fullWidth size="small">
                        <Select
                            displayEmpty
                            value={form.type}
                            onChange={e => onTypeChange(e.target.value as DeliverableType)}
                            sx={selectSx}
                            MenuProps={menuProps}>
                            <MenuItem value="" disabled sx={{ color: '#666' }}>
                                Select type…
                            </MenuItem>
                            {filteredTypes.map(t => (
                                <MenuItem key={t.value} value={t.value}>
                                    {t.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* Platform */}
                {showPlatform && (
                    <Box>
                        <Typography sx={labelSx}>Platform</Typography>
                        <FormControl fullWidth size="small">
                            <Select
                                displayEmpty
                                value={form.platform}
                                onChange={e => set('platform', e.target.value as DeliverablePlatform)}
                                sx={selectSx}
                                MenuProps={menuProps}>
                                <MenuItem value="" disabled sx={{ color: '#666' }}>
                                    Select…
                                </MenuItem>
                                {filteredPlatforms.map(p => (
                                    <MenuItem key={p.value} value={p.value}>
                                        {p.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                )}

                {/* Duration */}
                <Box>
                    <Typography sx={labelSx}>Duration</Typography>
                    <FormControl fullWidth size="small">
                        <Select
                            value={form.duration_range}
                            onChange={e => set('duration_range', e.target.value as DeliverableDuration)}
                            sx={selectSx}
                            MenuProps={menuProps}>
                            {DURATIONS.map(d => (
                                <MenuItem key={d.value} value={d.value}>
                                    {d.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* Notes */}
                <Box>
                    <Typography sx={labelSx}>Notes (optional)</Typography>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="e.g. Feature product in first 3s"
                        value={form.notes}
                        onChange={e => set('notes', e.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                bgcolor: '#1e1e1e',
                                borderRadius: 1.5,
                                color: '#fff',
                                fontSize: 13,
                                '& fieldset': { border: 'none' },
                                '&.Mui-focused fieldset': { border: '1px solid #ffc96540' },
                            },
                        }}
                    />
                </Box>
            </Box>

            {/* Multiple uploads toggle */}
            <Box sx={{ borderTop: '1px solid #2a2a2a', pt: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                        <Typography sx={labelSx}>Multiple uploads</Typography>
                        <Typography sx={{ fontSize: 11, color: '#666', mt: 0.25 }}>
                            Require multiple versions (e.g. English + Farsi)
                        </Typography>
                    </Box>
                    <Switch
                        size="small"
                        checked={form.multiUploadEnabled}
                        onChange={e => set('multiUploadEnabled', e.target.checked)}
                        sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#ffc965' },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#ffc96550' },
                        }}
                    />
                </Box>

                {form.multiUploadEnabled && (
                    <Box sx={{ mt: 1.25, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {/* Count */}
                        <Box>
                            <Typography sx={{ ...labelSx, mb: 0.75 }}>Number of uploads</Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                {[2, 3, 4, 5].map(n => (
                                    <Button
                                        key={n}
                                        size="small"
                                        onClick={() => onMultiUploadCountChange(n)}
                                        sx={{
                                            minWidth: 32,
                                            px: 0.75,
                                            borderRadius: 1,
                                            fontWeight: 700,
                                            fontSize: 12,
                                            textTransform: 'none',
                                            ...(form.multiUploadCount === n
                                                ? { bgcolor: '#ffc965', color: '#1a1a1a' }
                                                : { bgcolor: '#1e1e1e', color: '#9e9e9e' }),
                                        }}>
                                        {n}
                                    </Button>
                                ))}
                            </Box>
                        </Box>

                        {/* Labels */}
                        <Box>
                            <Typography sx={{ ...labelSx, mb: 0.75 }}>Labels (required)</Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                {Array.from({ length: form.multiUploadCount }).map((_, i) => (
                                    <TextField
                                        key={i}
                                        size="small"
                                        fullWidth
                                        placeholder={`Upload ${i + 1} label (e.g. English)`}
                                        value={form.multiUploadLabels[i] ?? ''}
                                        onChange={e => {
                                            const labels = [...form.multiUploadLabels];
                                            labels[i] = e.target.value;
                                            set('multiUploadLabels', labels);
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                bgcolor: '#1e1e1e',
                                                borderRadius: 1.5,
                                                color: '#fff',
                                                fontSize: 12,
                                                '& fieldset': { border: 'none' },
                                                '&.Mui-focused fieldset': { border: '1px solid #ffc96540' },
                                            },
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    </Box>
                )}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                    size="small"
                    onClick={onCancel}
                    sx={{
                        textTransform: 'none',
                        color: '#9e9e9e',
                        fontSize: 12,
                        '&:hover': { bgcolor: 'transparent', color: '#fff' },
                    }}>
                    Cancel
                </Button>
                <Button
                    size="small"
                    disabled={!canSave}
                    onClick={onSave}
                    startIcon={<CheckIcon sx={{ fontSize: '14px !important' }} />}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: 12,
                        borderRadius: 1.5,
                        px: 1.5,
                        background: canSave ? 'linear-gradient(90deg, #ffc965, #ff8c00)' : undefined,
                        bgcolor: !canSave ? '#2a2a2a' : undefined,
                        color: canSave ? '#1a1a1a' : '#555',
                        '&:hover': { opacity: 0.9 },
                        '&.Mui-disabled': { opacity: 0.4 },
                    }}>
                    {isEdit ? 'Save' : 'Add'}
                </Button>
            </Box>
        </Box>
    );
}
